"use client";

import { useEffect } from "react";
import { Stack, Divider, Box } from "@mui/material";
import ServicesHero from "@/sections/services/servicesHero";
import SmartContracts from "@/sections/services/smartContracts";
import InfraAndProtocols from "@/sections/services/infraAndProtocols";
import AISystems from "@/sections/services/aiSystems";
import ProductDev from "@/sections/services/productDev";
import GTMAndLegal from "@/sections/services/gtmAndLegal";
import ZkAndPqc from "@/sections/services/zkAndPqc";
import Footer from "@/components/layout/footer";
import theme from "@/theme/theme";
import Image from "next/image";

import servicesPageGradient from "@/assets/images/servicesPage/servicesPageGradients.webp";

export default function ServicesPage() {

    useEffect(() => {
        let cleanupFns: (() => void)[] = [];
        const cleanup = () => {
            cleanupFns.forEach((fn) => fn());
            cleanupFns = [];
        };

        // On a slow connection the target section may not even be rendered
        // yet when the navigation happens, and the page keeps growing and
        // shifting for many seconds as images load. A fixed set of timers
        // misses both, so instead: watch until the section exists, scroll to
        // it smoothly once, then pin it with instant corrections whenever
        // layout shift displaces it, until things settle, the user takes
        // over, or a hard time cap expires.
        const runScrollSequence = (targetHash?: string) => {
            cleanup();

            const hash = targetHash ?? window.location.hash;
            if (!hash || hash.length < 2) return;

            let firstScrollDone = false;
            let lastTop: number | null = null;

            const correct = () => {
                let element: HTMLElement | null = null;
                try {
                    element = document.querySelector<HTMLElement>(hash);
                } catch {
                    cleanup(); // malformed hash
                    return;
                }
                if (!element) return; // not rendered yet, keep watching
                const margin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
                const top = element.getBoundingClientRect().top;
                if (Math.abs(top - margin) <= 2) {
                    lastTop = top;
                    return; // in place
                }
                if (!firstScrollDone) {
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                    firstScrollDone = true;
                    lastTop = null;
                    return;
                }
                // While the position is still moving (smooth scroll animating
                // or layout actively shifting) leave it alone; only snap it
                // back once it has settled in the wrong place.
                if (lastTop !== null && Math.abs(top - lastTop) <= 1) {
                    element.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "start" });
                    lastTop = null;
                    return;
                }
                lastTop = top;
            };

            correct();
            const interval = setInterval(correct, 300);
            cleanupFns.push(() => clearInterval(interval));

            const cap = setTimeout(cleanup, 15000);
            cleanupFns.push(() => clearTimeout(cap));

            // Hand control back the moment the user touches or wheels. A
            // touchstart can never be residue of the tap that triggered us
            // (the click fires after the finger lifts), so it cancels
            // immediately; wheel gets a short grace period to absorb
            // trackpad inertia from before the click.
            const startedAt = performance.now();
            const onWheel = () => {
                if (performance.now() - startedAt < 300) return;
                cleanup();
            };
            window.addEventListener("wheel", onWheel, { passive: true });
            window.addEventListener("touchstart", cleanup, { passive: true });
            cleanupFns.push(() => {
                window.removeEventListener("wheel", onWheel);
                window.removeEventListener("touchstart", cleanup);
            });
        };

        // Same-page menu taps navigate via pushState, which fires no
        // hashchange event and gets a single, easily-disrupted scroll from the
        // router. Watch anchor clicks directly and drive the scroll ourselves.
        const onDocumentClick = (event: MouseEvent) => {
            const target = event.target as Element | null;
            const anchor = target?.closest?.("a[href*='#']");
            if (!anchor) return;
            const href = anchor.getAttribute("href") ?? "";
            const hashIndex = href.indexOf("#");
            const path = href.slice(0, hashIndex);
            const hash = href.slice(hashIndex);
            if (hash.length < 2) return;
            if (path && path !== window.location.pathname && !path.endsWith(window.location.pathname)) return;
            runScrollSequence(hash);
        };

        const onHashChange = () => runScrollSequence();

        runScrollSequence();
        document.addEventListener("click", onDocumentClick, true);
        window.addEventListener("hashchange", onHashChange);

        return () => {
            cleanup();
            document.removeEventListener("click", onDocumentClick, true);
            window.removeEventListener("hashchange", onHashChange);
        };
    }, []);

    const scrollOffset = { 
        scrollMarginTop: { xs: "70px", md: "120px" },
        width: '100%' 
    };

    return (
        <Stack width={"100%"} alignItems={"center"} gap={2}>
            <ServicesHero />
            <Stack width={"100%"} alignItems={"center"} position={"relative"} paddingBottom={10}>
                <Stack width={"100%"} height={"100%"} sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 1,
                }}>
                    <Image src={servicesPageGradient} alt="services page gradient" width={1000} height={1000} style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        
                    }} />
                </Stack>
                <Stack width={"100%"} alignItems={"center"} position={"relative"} overflow={"hidden"} sx={{
                    zIndex: 2,
                    pt: { xs: "60px", md: "100px" },
                }}>
                    <Box id="smart-contracts"  sx={scrollOffset}>
                        <SmartContracts />
                    </Box>
                    <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.17)", marginY: { xs: 6, lg: 10 }, marginX: "auto", width: "80%" }} />
                    <Box id="infra-and-protocols" sx={scrollOffset}>
                        <InfraAndProtocols />
                    </Box>
                    <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.17)", marginY: { xs: 6, lg: 10 }, marginX: "auto", width: "80%" }} />
                    <Box id="ai-systems" sx={scrollOffset}>
                        <AISystems />
                    </Box>
                    <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.17)", marginY: { xs: 6, lg: 10 }, marginX: "auto", width: "80%" }} />
                    <Box id="product-dev" sx={scrollOffset}>
                        <ProductDev />
                    </Box>
                    <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.17)", marginY: { xs: 6, lg: 10 }, marginX: "auto", width: "80%" }} />
                    <Box id="zk-and-pqc" sx={scrollOffset}>
                        <ZkAndPqc />
                    </Box>
                    <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.17)", marginY: { xs: 6, lg: 10 }, marginX: "auto", width: "80%" }} />
                    <Box id="gtm-and-legal" sx={scrollOffset}>
                        <GTMAndLegal />
                    </Box>
                </Stack>
            </Stack>
            <Stack width={"100%"} alignItems={"center"} position={"relative"} sx={{
                borderTop: `1px solid ${theme.palette.brand.border1.main}`,
                zIndex: 2,
                backgroundColor: "#0B0B0B",
            }}>
                <Footer />
            </Stack>
        </Stack>
    );
}
